import { useEffect, useRef, useState } from "react";
import {
    IStyledComponent,
    css,
    keyframes,
    styled,
    useTheme,
} from "styled-components";

interface LoadingProp {
    $loading: boolean;
}

const LoadingAnimation = keyframes`
    0%{
        background-position: right/300% 100%;
    }50%{
        background-position: left;
    }100%{
        background-position: right/300% 100%;
    }
`;

const StatsContainer = styled.div`
    display: flex;
    align-items: center;
`;

const StatNameValuesContainer = styled.div`
    display: flex;
    gap: 20px;
`;

const Stat = styled.div<LoadingProp>`
    display: flex;
`;

const StatIcon = styled.img<LoadingProp>`
    width: 100px;
    height: 100px;
    padding: 0px 40px;
`;

const StatName = styled.div<LoadingProp>`
    border-radius: 5px;
    font-weight: 600;
    text-transform: uppercase;
    ${({ $loading, theme }) =>
        $loading
            ? css`
                  background: linear-gradient(90deg, #00e2c8 0%, #1e1e1e 100%)
                      right/200% 100%;
                  animation: ${LoadingAnimation};
                  animation-iteration-count: infinite;
                  animation-timing-function: ease-in-out;
                  animation-duration: ${theme.animationTime.long}s;
                  height: 20px;
              `
            : css``}
`;

const StatNumber = styled.div<LoadingProp>`
    border-radius: 5px;
    font-weight: 600;
    ${({ $loading, theme }) =>
        $loading
            ? css`
                  background: linear-gradient(90deg, #00e2c8 0%, #1e1e1e 100%)
                      right/200% 100%;
                  animation: ${LoadingAnimation};
                  animation-iteration-count: infinite;
                  animation-timing-function: ease-in-out;
                  animation-duration: ${theme.animationTime.long}s;
                  height: 20px;
              `
            : css`
                  font-size: ${theme.fontSize.medium}px;
                  color: ${theme.colors.discordBlue};
              `}
`;

const StatNameAndNumberAnim = keyframes`
    0%{
        transform: translateX(400%);
        opacity: 0;
    }
    25%{
        opacity: 1;
    } 
    30%{
        transform: translateX(0);
    }70%{
        transform: translateX(0);
    }75%{
        opacity: 1;
    }100%{
        opacity: 0;
        transform: translateX(-500px);
    } 
`;

const StatNameAndNumber = styled.div`
    display: flex;
    flex-direction: column-reverse;
    justify-content: center;
    align-items: center;
    ${({ theme }) => css`
        @media screen and (max-width: ${theme.breakpoint}px) {
            opacity: 0;
            transform: translateX(400%);
            animation: ${StatNameAndNumberAnim} 5s;
            animation-fill-mode: forwards;
            animation-iteration-count: 1;
        }
    `}
`;

const DataDisplayDefaults = {
    StyledDataContainer: StatsContainer,
    containerProps: {},
    StyledStatsContainer: StatNameValuesContainer,
    statsContainerProps: {},
    StyledDataStat: Stat,
    statProps: {},
    StyledDataStatName: StatName,
    statNameProps: {},
    StyledDataStatNumber: StatNumber,
    statNumberProps: {},
    StyledDataStatAndNumber: StatNameAndNumber,
    statNameAndNumberProps: {},
    StyledDataStatIcon: StatIcon,
    statIconProps: {},
    stats: [
        { name: "example", number: 0, icon: "example" },
        { name: "example", number: 0, icon: "example" },
        { name: "example", number: 0, icon: "example" },
        { name: "example", number: 0, icon: "example" },
    ],
    loading: true,
};

interface DataDisplayProps {
    StyledDataContainer?: IStyledComponent<any, any>;
    containerProps?: { [key: string]: unknown };
    StyledStatNameValuesContiner?: IStyledComponent<any, any>;
    nameValuesContainerProps?: { [key: string]: unknown };
    StyledDataStat?: IStyledComponent<any, any>;
    statProps?: { [key: string]: unknown };
    StyledDataStatName?: IStyledComponent<any, any>;
    statNameProps?: { [key: string]: unknown };
    StyledDataStatNumber?: IStyledComponent<any, any>;
    statNumberProps?: { [key: string]: unknown };
    StyledDataStatAndNumber?: IStyledComponent<any, any>;
    statNameAndNumberProps?: { [key: string]: unknown };
    StyledDataStatIcon?: IStyledComponent<any, any>;
    statIconProps?: { [key: string]: unknown };
    stats?: {
        name: string;
        number: number;
        icon: string;
    }[];
    loading?: boolean;
}

export const DataDisplay = ({
    StyledDataContainer = DataDisplayDefaults.StyledDataContainer,
    StyledStatNameValuesContiner = DataDisplayDefaults.StyledStatsContainer,
    StyledDataStat = DataDisplayDefaults.StyledDataStat,
    StyledDataStatIcon = DataDisplayDefaults.StyledDataStatIcon,
    StyledDataStatName = DataDisplayDefaults.StyledDataStatName,
    StyledDataStatNumber = DataDisplayDefaults.StyledDataStatNumber,
    StyledDataStatAndNumber = DataDisplayDefaults.StyledDataStatAndNumber,

    containerProps = DataDisplayDefaults.containerProps,
    nameValuesContainerProps = DataDisplayDefaults.statsContainerProps,
    statProps = DataDisplayDefaults.statProps,
    statNameProps = DataDisplayDefaults.statNameProps,
    statNumberProps = DataDisplayDefaults.statNumberProps,
    statNameAndNumberProps = DataDisplayDefaults.statNameAndNumberProps,
    statIconProps = DataDisplayDefaults.statIconProps,

    stats = DataDisplayDefaults.stats,
    loading = DataDisplayDefaults.loading,
}: DataDisplayProps) => {
    const theme = useTheme();
    const [index, setIndex] = useState(0);
    const [onMobile, setOnMobile] = useState(false);
    const statsRef = useRef(stats);
    useEffect(() => {
        const t = setInterval(() => {
            setIndex((index) =>
                index + 1 < statsRef.current.length ? index + 1 : 0
            );
        }, 5000);
        return () => clearInterval(t);
    }, []);
    useEffect(() => {
        setOnMobile(window.innerWidth <= theme.breakpoint);
    }, [theme.breakpoint]);
    return (
        <StyledDataContainer {...containerProps}>
            <StyledStatNameValuesContiner {...nameValuesContainerProps}>
                {onMobile ? (
                    <StyledDataStat
                        key={index}
                        $loading={loading}
                        {...statProps}
                    >
                        <StyledDataStatAndNumber {...statNameAndNumberProps}>
                            <StyledDataStatName
                                $loading={loading}
                                {...statNameProps}
                            >
                                {!loading && stats[index].name}
                            </StyledDataStatName>
                            <StyledDataStatNumber
                                $loading={loading}
                                {...statNumberProps}
                            >
                                {!loading &&
                                    stats[index].number.toLocaleString()}
                            </StyledDataStatNumber>
                        </StyledDataStatAndNumber>
                    </StyledDataStat>
                ) : (
                    stats.map((stat, idx) => {
                        return (
                            <StyledDataStat
                                key={idx}
                                $loading={loading}
                                {...statProps}
                            >
                                <StyledDataStatAndNumber
                                    {...statNameAndNumberProps}
                                >
                                    <StyledDataStatName
                                        $loading={loading}
                                        {...statNameProps}
                                    >
                                        {!loading && stat.name}
                                    </StyledDataStatName>
                                    <StyledDataStatNumber
                                        $loading={loading}
                                        {...statNumberProps}
                                    >
                                        {!loading &&
                                            stat.number.toLocaleString()}
                                    </StyledDataStatNumber>
                                </StyledDataStatAndNumber>
                            </StyledDataStat>
                        );
                    })
                )}
            </StyledStatNameValuesContiner>
        </StyledDataContainer>
    );
};
